import { Component, createContext } from 'react';

import type { ApolloError } from '@apollo/client';
import * as Sentry from '@sentry/react';
import compose from 'lodash.flowright';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import { ErrorPage } from '@/components/errorPage/errorPage';
import { stratumnErrorSchema } from '@/components/errorPage/stratumn-error.schema';
import { ROUTE_WORKFLOW_DASHBOARD } from '@/constant/routes';
import { is401Error } from '@/gql/client/errorLink';

type ErrorContextType = {
  handleError: (
    name?: string,
    paramId?: string,
    path?: string,
    error?: ApolloError
  ) => void;
};

export const ErrorContext = createContext<ErrorContextType>({
  // @ts-expect-error we'll assume that handleError is defined when use since since children of the error boundary are wrapped with withError
  handleError: undefined
});

export type WithError<T> = T & {
  errorContext: ErrorContextType;
};

export const withError = Component => props => (
  <ErrorContext.Consumer>
    {({ handleError }) => (
      <Component errorContext={{ handleError }} {...props} />
    )}
  </ErrorContext.Consumer>
);

type TraceError = {
  description?: React.ReactNode;
  label?: string;
  path?: string;
  error?: ApolloError;
};

type State = {
  error: Error | null;
  customError: TraceError | null;
};

type Props = {
  children: React.ReactNode;
  // TODO: install @types/react-router-dom
  history: {
    push: (path: string) => void;
  };
};
export class ErrorBoundary extends Component<Props, State> {
  static propTypes = {
    children: PropTypes.node.isRequired,
    history: PropTypes.object.isRequired
  };

  defaultState = {
    error: null,
    customError: null
  } as const satisfies State;

  state: State = this.defaultState;

  setDocTitle() {
    if (this.hasError()) document.title = 'Oops! Page not found - Trace';
  }

  hasError() {
    const { error, customError } = this.state;
    return error || customError;
  }

  componentDidMount() {
    this.setDocTitle();
  }

  componentDidUpdate() {
    this.setDocTitle();
  }

  componentDidCatch(error, info) {
    this.setState({ error });
    Sentry.captureException(error, {
      extra: info
    });
  }

  handleCustomError = (
    name?: string,
    paramId?: string,
    path?: string,
    providedError?: ApolloError
  ) => {
    if (is401Error(providedError)) return;

    this.setState({
      customError: {
        description: name && paramId && (
          <p className="text-wrap">
            The {name} with the ID{' '}
            <b className="rounded-md border border-border bg-accent px-2 py-0.5 font-medium">
              {paramId}
            </b>{' '}
            does not exist.
          </p>
        ),
        label: name && paramId && 'Take me to the home page',
        path,
        error: providedError
      }
    });
  };

  goTo = route => {
    this.setState(this.defaultState);
    this.props.history.push(route);
  };

  reloadPage = () => {
    window.location.reload();
    this.setState(this.defaultState);
  };

  renderErrorPage = () => {
    const { error, customError } = this.state;

    const stratumnError = stratumnErrorSchema.safeParse(
      // @ts-expect-error
      customError?.error?.cause?.originalError
    );

    let description = `${error?.name}: ${error?.message}`;
    let title: string | undefined;
    let errorMessage: string | undefined;

    if (customError) {
      description = customError.description as string;
      title = customError.label;
      errorMessage =
        customError.error?.cause?.stack || error?.stack || undefined;
    }

    if (stratumnError.success) {
      description = stratumnError.data._message;
      title = `${stratumnError.data.statusCode}: ${stratumnError.data.service}: ${stratumnError.data.uniqueCode}`;
      errorMessage = `Debugging info:
${JSON.stringify(stratumnError.data.extra, null, 2)}`;
    }

    return (
      <ErrorPage
        buttonLabel={customError?.label}
        description={description}
        errorMessage={errorMessage}
        onHomeClick={() =>
          this.goTo(customError?.path || ROUTE_WORKFLOW_DASHBOARD)
        }
        onReloadPageClick={this.reloadPage}
        title={title}
      />
    );
  };

  render() {
    const { children } = this.props;

    return (
      <ErrorContext.Provider value={{ handleError: this.handleCustomError }}>
        {this.hasError() ? this.renderErrorPage() : children}
      </ErrorContext.Provider>
    );
  }
}

export default compose(withRouter)(ErrorBoundary);
